/**
 * Supabase Client Integration for Skin Health Dashboard
 * Handles Authentication and Data Syncing
 */

// ------------------------------------------------------------------
// CONFIGURATION (Replace with your keys from Supabase Dashboard)
// ------------------------------------------------------------------
// ------------------------------------------------------------------
// CONFIGURATION
// ------------------------------------------------------------------
const SUPABASE_CONFIG = {
    url: 'https://qkjmqtvxqpdqnsfubihm.supabase.co',
    key: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InFram1xdHZ4cXBkcW5zZnViaWhtIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDMzMTg3NzQsImV4cCI6MjA1ODg5NDc3NH0.5t9y45Uh8TEplvnltaL6LnyLOIe4YjqbvGPSw5SMSGk'
};

// Global Client
let sbClient = null;

/**
 * Initialize Supabase Logic
 * Called automatically if SDK is present
 */
function initSupabaseClient() {
    if (typeof supabase !== 'undefined' && SUPABASE_CONFIG.url.startsWith('http')) {
        sbClient = supabase.createClient(SUPABASE_CONFIG.url, SUPABASE_CONFIG.key);

        // Listen for auth state changes
        sbClient.auth.onAuthStateChange((event, session) => {
            if (event === 'SIGNED_IN' && session) {
                console.log('✅ Supabase: User Signed In', session.user.email);
                updateUiForLoggedInUser(session.user);
                syncLocalDataToCloud(session.user.id, session.user.email);
            }
        });

        // HIDDEN STATE CHECK: If already saved or logged in, hide signup
        const isSaved = localStorage.getItem('drtowel_quiz_saved');
        if (isSaved) {
            const guestMsg = document.getElementById('GuestSaveMsg');
            if (guestMsg) guestMsg.style.display = 'none';
        }

    } else {
        console.warn('⚠️ Supabase not configured. Please add URL/Key in skin-quiz-supabase.js');
    }
}

/**
 * Log a lead when a name is entered or quiz finished (Anonymous Tracking)
 * Helps track traffic even if they don't provide an email.
 */
async function logQuizLead(name, type = 'pdp_name_entered', data = {}) {
    if (!sbClient) return;

    const payload = {
        user_name: name,
        quiz_type: type,
        recorded_at: new Date().toISOString(),
        ...data
    };

    // Try to insert. If it fails due to RLS or constraints, we just fail silently.
    const { error } = await sbClient
        .from('quiz_submissions')
        .insert([payload]);

    if (!error) {
        console.log(`📊 Quiz Event Logged [${type}]:`, name);
    } else {
        console.warn('Quiz logging error:', error.message);
    }
}

/**
 * Save with Email (Magic Link)
 * Sends a verification email and stores data when they click the link
 */
async function saveWithEmail() {
    if (!sbClient) {
        alert("Service loading... please try again in a moment.");
        return;
    }

    const emailInput = document.getElementById('SaveEmailInput');
    const statusEl = document.getElementById('EmailStatus');
    const formEl = document.getElementById('EmailSignupForm');
    const email = emailInput ? emailInput.value.trim() : '';

    if (!email || !email.includes('@')) {
        if (statusEl) {
            statusEl.style.display = 'block';
            statusEl.style.color = '#c00';
            statusEl.textContent = 'Please enter a valid email';
        }
        return;
    }

    // Show loading state
    const btn = document.getElementById('BtnSaveEmail');
    if (btn) {
        btn.textContent = 'Saving...';
        btn.disabled = true;
    }

    // --- ENHANCED SAVE LOGIC: Save User 2's data + Partner's data ---
    const allAnswers = SkinQuiz.getStoredAnswers() || {};
    const q13Answers = allAnswers['13'];

    if (q13Answers && q13Answers.length) {
        const score = SkinQuiz.calculateScore(13, q13Answers);

        // Get User 2's name from localStorage (they entered it during the quiz)
        const user2Name = localStorage.getItem('pdp_user_name') || '';

        // Get Partner's data from URL parameters (User 1 who shared the link)
        const params = new URLSearchParams(window.location.search);
        const partnerName = params.get('sharerName') || localStorage.getItem('pdp_shared_partner_name') || '';
        const partnerScore = params.get('partnerScore') || localStorage.getItem('pdp_shared_partner_score') || '';
        const partnerAnswersParam = params.get('partnerAnswers') || localStorage.getItem('pdp_shared_partner_answers') || '';
        const partnerAnswers = partnerAnswersParam ? partnerAnswersParam.split('').map(Number) : [];

        // Calculate household comparison data
        let householdData = null;
        if (partnerScore && partnerAnswers.length > 0) {
            const comparison = SkinQuiz.compareHouseholds(
                parseInt(partnerScore),
                score,
                partnerAnswers,
                q13Answers
            );
            householdData = {
                scenario: comparison.scenario,
                reason: comparison.reason,
                average_score: Math.round((parseInt(partnerScore) + score) / 2)
            };
        }

        // Payload for User 2 (the person entering their email)
        const user2Payload = {
            user_id: email, // Use email as ID for lead-gen style saves
            email: email,
            quiz_type: 'towel_hygiene_partner',
            score: score,
            answers: q13Answers,
            user_name: user2Name,
            partner_name: partnerName, // Who they rated
            recorded_at: new Date().toISOString(),
            household_data: householdData
        };

        // Save User 2's data
        const { error: dbError } = await sbClient
            .from('quiz_submissions')
            .insert([user2Payload]);

        if (dbError) {
            console.error('Database Save Error', dbError);
            if (statusEl) {
                statusEl.style.display = 'block';
                statusEl.style.color = '#c00';
                statusEl.textContent = 'Database error. Please try again.';
            }
            if (btn) {
                btn.textContent = 'Unlock Insights';
                btn.disabled = false;
            }
            return;
        }

        // ALSO save Partner's data (User 1) if we have it
        if (partnerScore && partnerAnswers.length > 0) {
            // Get the partner's partner name (the person User 1 rated - which is User 2)
            const partnerPartnerName = params.get('sharerPartnerName') || localStorage.getItem('pdp_partner_name') || user2Name;

            const partnerPayload = {
                user_id: `partner_${email}_${Date.now()}`, // Temporary ID until they claim it
                email: null, // Partner hasn't provided email yet
                quiz_type: 'towel_hygiene_partner',
                score: parseInt(partnerScore),
                answers: partnerAnswers,
                user_name: partnerName,
                partner_name: partnerPartnerName, // Who User 1 rated
                recorded_at: new Date().toISOString(),
                household_data: householdData,
                linked_to_email: email // Link to User 2's record
            };

            // Save partner's data (non-blocking)
            await sbClient
                .from('quiz_submissions')
                .insert([partnerPayload])
                .catch(err => console.log('Partner data save skipped:', err));
        }

        // Success State
        if (formEl) formEl.style.display = 'none';
        localStorage.setItem('drtowel_quiz_saved', 'true');
        if (statusEl) {
            statusEl.style.display = 'block';
            statusEl.style.color = '#4CAF50';
            statusEl.style.transition = 'opacity 0.5s ease';
            statusEl.style.opacity = '1';
            statusEl.innerHTML = householdData
                ? '✓ Household results saved!'
                : '✓ Progress saved.';

            // Fade out after 3 seconds
            setTimeout(() => {
                statusEl.style.opacity = '0';
                setTimeout(() => {
                    statusEl.style.display = 'none';
                }, 500);
            }, 3000);
        }

        const toastMsg = householdData ? 'Household results saved!' : 'Progress saved.';
        showToast(toastMsg);

        // Optional: Trigger magic link in background so they can login later
        sbClient.auth.signInWithOtp({
            email: email,
            options: { emailRedirectTo: window.location.href }
        }).catch(err => console.log('OTP attempt ignored', err));

    } else {
        alert("No quiz result found to save.");
        if (btn) {
            btn.textContent = 'Unlock Insights';
            btn.disabled = false;
        }
    }
}

/**
 * Sync LocalStorage Data to Supabase (Shopify Auth Version)
 */
async function syncLocalDataToCloud(userId, userEmail) {
    if (!sbClient || !userId) return;

    // 1. Get Local Data
    const allAnswers = SkinQuiz.getStoredAnswers() || {};

    // 2. Iterate and Upload
    const q13Answers = allAnswers['13'];
    if (q13Answers && q13Answers.length) {
        const score = SkinQuiz.calculateScore(13, q13Answers);

        const payload = {
            user_id: userId, // This is now the SHOPIFY ID, not Supabase ID
            email: userEmail, // Optional tracking
            quiz_type: 'towel_hygiene_partner',
            score: score,
            answers: q13Answers,
            recorded_at: new Date().toISOString()
        };

        // Note: For this to work with 'anon' key, you must DISABLE strict RLS policies
        // or create a policy that allows INSERT for 'anon' role.
        const { error } = await sbClient
            .from('quiz_submissions')
            .insert([payload]);

        if (!error) {
            console.log('✅ Data Synced to Cloud');
            showToast('Result saved to dashboard!');
            // After sync, fetch history to update UI
            fetchHistory(userId);
        } else {
            console.error('Sync Error', error);
        }
    }
}

/**
 * Fetch & Display History
 */
async function fetchHistory(userId) {
    if (!sbClient) return;

    // Fetch last 5 results
    const { data, error } = await sbClient
        .from('quiz_submissions')
        .select('*')
        .eq('user_id', userId)
        .eq('quiz_type', 'towel_hygiene_partner')
        .order('recorded_at', { ascending: false })
        .limit(5);

    if (error) {
        console.error('History Fetch Error', error);
        return;
    }

    if (data && data.length > 0) {
        renderHistoryList(data);
    }
}

function renderHistoryList(historyData) {
    // 1. Find where to put it.
    // We'll insert it after the "Save Result" button in the modal or in the dedicated "Facts" section.
    const container = document.getElementById('QmContainer');
    if (!container) return;

    // Check if we already have a history section
    let historySection = document.getElementById('QmHistorySection');
    if (!historySection) {
        historySection = document.createElement('div');
        historySection.id = 'QmHistorySection';
        historySection.style.padding = '0 32px 30px';
        historySection.style.borderTop = '1px solid #eee';
        historySection.style.marginTop = '20px';
        container.appendChild(historySection);
    }

    const rows = historyData.map(entry => {
        const date = new Date(entry.recorded_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        const score = entry.score;
        let color = '#990000'; // compromised
        if (score >= 80) color = '#1a5c68'; // optimal
        else if (score >= 50) color = '#d97706'; // stable

        return `
            <div style="display:flex; justify-content:space-between; align-items:center; padding: 12px 0; border-bottom: 1px solid #f9f9f9;">
                <span style="font-size:14px; color:#555;">${date}</span>
                <span style="font-weight:600; font-size:14px; color:${color};">${score}/100</span>
            </div>
        `;
    }).join('');

    historySection.innerHTML = `
        <h4 style="margin:20px 0 10px; font-size:14px; text-transform:uppercase; letter-spacing:0.5px; color:#999;">History</h4>
        <div>${rows}</div>
    `;
}

/**
 * UI Updates
 */
function updateUiForLoggedInUser(user) {
    // Find the Connect Button and change state
    const btn = document.getElementById('BtnConnectGoogle');
    if (btn) {
        btn.innerHTML = `<span style="color:#4CAF50; font-weight:bold;">✓ Saved</span>`;
        btn.style.pointerEvents = 'none';
        btn.style.border = '1px solid #4CAF50';
        btn.style.color = '#4CAF50';
    }

    // Optional: Hide "Guest" elements
    const guestMsg = document.getElementById('GuestSaveMsg');
    if (guestMsg) guestMsg.style.display = 'none';

    // Load history immediately
    fetchHistory(user.id);
}

/**
 * Show Toast Notification
 */
function showToast(message) {
    const toast = document.getElementById('QuizToast');
    const msgEl = document.getElementById('ToastMsg');
    if (toast && msgEl) {
        msgEl.innerText = message;
        toast.classList.add('is-active');
        setTimeout(() => toast.classList.remove('is-active'), 2500);
    }
}

// ------------------------------------------------------------------
// Auto-Init on Load
// ------------------------------------------------------------------
document.addEventListener("DOMContentLoaded", function () {
    // Wait for CDN to load
    const interval = setInterval(() => {
        if (typeof supabase !== 'undefined') {
            clearInterval(interval);
            initSupabaseClient();
        }
    }, 100);
});
